<template>
  <div class="campaign-moderation campaign-moderation--rtb">
    <header class="campaign-moderation__header">
      <h1 class="campaign-moderation__title">Модерация RTB кампаний</h1>
    </header>

    <section v-if="campaigns.length === 0" class="campaign-moderation__empty">
      <p>Нет RTB кампаний на модерации</p>
    </section>

    <section v-else class="campaign-moderation__list">
      <article
        v-for="campaign in campaigns"
        :key="campaign.id"
        class="moderation-card moderation-card--rtb-campaign"
      >
        <div class="moderation-card__main">
          <div class="moderation-card__titles">
            <h3 class="moderation-card__title" :title="campaign.name">{{ campaign.name }}</h3>
            <div class="moderation-card__meta-row">
              <span class="meta-id">#{{ campaign.id }}</span>
              <span
                class="status-pill"
                :class="statusPillClass(campaign.system_status || campaign.status)"
              >
                {{ statusLabel(campaign.system_status || campaign.status) }}
              </span>
              <span class="meta-created">{{ formatDate(campaign.created_at) }}</span>
            </div>
          </div>

          <div class="moderation-card__chips">
            <span v-if="campaign.campaign_type === 'package'" class="chip chip--package">Пакет</span>
            <span v-if="campaign.campaign_type === 'poi'" class="chip chip--package">POI</span>
            <span
              v-if="campaign.poi_profile?.layer?.name"
              class="chip chip--truncate"
              :title="campaign.poi_profile.layer.name"
            >
              {{ campaign.poi_profile.layer.name }}
            </span>
            <span
              v-if="campaign.package_profile?.screen_package?.name"
              class="chip chip--truncate"
              :title="campaign.package_profile.screen_package.name"
            >
              {{ campaign.package_profile.screen_package.name }}
            </span>
            <span v-if="campaign.created_by" class="chip chip--truncate" :title="campaign.created_by.email">
              {{ campaign.created_by.email }}
            </span>
            <span v-if="campaign.advertiser" class="chip chip--truncate" :title="campaign.advertiser.name">
              {{ campaign.advertiser.name }}
            </span>
            <span v-if="campaign.brand" class="chip chip--truncate" :title="campaign.brand.name">
              {{ campaign.brand.name }}
            </span>
            <span class="chip">{{ formatPeriod(campaign.start_date, campaign.end_date) }}</span>
          </div>

          <div class="moderation-card__stats">
            <div class="stat">
              <div class="stat-label">Бюджет</div>
              <div class="stat-value">{{ formatPrice(campaign.budget_total_limit) }}</div>
            </div>
            <div class="stat">
              <div class="stat-label">Дневной</div>
              <div class="stat-value">{{ formatPrice(campaign.budget_daily_limit) }}</div>
            </div>
            <div class="stat">
              <div class="stat-label">Показы (всего)</div>
              <div class="stat-value">{{ campaign.impr_total_limit ? formatNumber(campaign.impr_total_limit) : '∞' }}</div>
            </div>
            <div class="stat">
              <div class="stat-label">Показы (дн.)</div>
              <div class="stat-value">{{ campaign.impr_daily_limit ? formatNumber(campaign.impr_daily_limit) : '∞' }}</div>
            </div>
          </div>
        </div>

        <div class="moderation-card__actions">
          <a :href="campaignLink(campaign)" class="act act--view" title="Просмотр и редактирование">Просмотр</a>
          <button @click="approveCampaign(campaign)" class="act act--approve" :disabled="processingId === campaign.id">
            {{ processingId === campaign.id ? '...' : 'Подтвердить' }}
          </button>
          <button @click="showRejectModal(campaign)" class="act act--reject" :disabled="processingId === campaign.id">
            Отклонить
          </button>
        </div>
      </article>
    </section>

    <div v-if="showRejectDialog" class="modal-overlay" @click.self="closeRejectModal">
      <div class="modal-content">
        <h3>Отклонить RTB кампанию</h3>
        <p><strong>{{ campaignToReject?.name }}</strong></p>
        <div class="modal-actions">
          <button @click="closeRejectModal" class="btn btn--secondary">Отмена</button>
          <button @click="rejectCampaign" class="btn btn--danger">Отклонить</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { formatInRtbTZ } from '../../../utils/timezones';
import { confirmAction } from '../../../composables/useConfirm';
import { useToast } from '../../../composables/useToast';
import { notifyHttpError } from '../../../utils/httpErrors';
import { RTB_STATUS_LABEL } from '../../../constants/rtbCampaigns';

export default {
  name: 'RtbCampaignModeration',
  props: {
    user: {
      type: Object,
      required: true,
    },
    initialCampaigns: {
      type: Array,
      default: undefined,
    },
  },
  data() {
    return {
      campaigns: [],
      processingId: null,
      showRejectDialog: false,
      campaignToReject: null,
    };
  },
  mounted() {
    this.campaigns = Array.isArray(this.initialCampaigns) ? [...this.initialCampaigns] : [];
  },
  methods: {
    campaignLink(campaign) {
      if (campaign.campaign_type === 'package') {
        return `/rtb/package-campaigns/${campaign.id}/edit`;
      }
      if (campaign.campaign_type === 'poi') {
        return `/rtb/poi-campaigns/${campaign.id}/edit`;
      }
      return `/rtb/campaigns/${campaign.id}/edit`;
    },
    statusLabel(status) {
      return RTB_STATUS_LABEL[status] || status || '—';
    },
    statusPillClass(status) {
      const normalized = String(status || '').toLowerCase();
      if (['active', 'approve', 'approved'].includes(normalized)) return 'status-pill--ok';
      if (['rejected', 'reject'].includes(normalized)) return 'status-pill--fail';
      if (['completed', 'exhausted'].includes(normalized)) return 'status-pill--archived';
      if (!status) return 'status-pill--idle';
      return 'status-pill--pending';
    },
    async approveCampaign(campaign) {
      const isConfirmed = await confirmAction({
        title: `Одобрить RTB-кампанию «${campaign.name}»?`,
        message: 'После одобрения кампания начнёт участвовать в аукционах в своём периоде.',
        confirmLabel: 'Одобрить',
      });
      if (!isConfirmed) return;
      this.processingId = campaign.id;
      try {
        const res = await axios.post(`/api/rtb/campaigns/${campaign.id}/approve`);
        if (res.data.success) {
          useToast().success(res.data.message || 'Кампания одобрена');
          this.campaigns = this.campaigns.filter(c => c.id !== campaign.id);
        } else {
          useToast().error(res.data.error || res.data.message || 'Не удалось подтвердить');
        }
      } catch (e) {
        notifyHttpError(e, 'Не удалось подтвердить кампанию');
      } finally {
        this.processingId = null;
      }
    },
    showRejectModal(campaign) {
      this.campaignToReject = campaign;
      this.showRejectDialog = true;
    },
    closeRejectModal() {
      this.showRejectDialog = false;
      this.campaignToReject = null;
    },
    async rejectCampaign() {
      if (!this.campaignToReject) return;
      this.processingId = this.campaignToReject.id;
      try {
        const res = await axios.post(`/api/rtb/campaigns/${this.campaignToReject.id}/reject`);
        if (res.data.success) {
          useToast().success(res.data.message || 'Кампания отклонена');
          this.campaigns = this.campaigns.filter(c => c.id !== this.campaignToReject.id);
          this.closeRejectModal();
        } else {
          useToast().error(res.data.error || res.data.message || 'Не удалось отклонить');
        }
      } catch (e) {
        notifyHttpError(e, 'Не удалось отклонить кампанию');
      } finally {
        this.processingId = null;
      }
    },
    formatDate(dateString) {
      return formatInRtbTZ(dateString);
    },
    formatPeriod(start, end) {
      if (!start || !end) return '—';
      const d = (str) => {
        const s = String(str).trim();
        const match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) return `${match[3]}.${match[2]}.${match[1]}`;
        const date = new Date(s);
        if (Number.isNaN(date.getTime())) return '—';
        const dd = String(date.getDate()).padStart(2, '0');
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const yyyy = date.getFullYear();
        return `${dd}.${mm}.${yyyy}`;
      };
      return `${d(start)} — ${d(end)}`;
    },
    formatPrice(price) {
      if (!price) return '0 ₸';
      return new Intl.NumberFormat('ru-RU', {
        style: 'currency',
        currency: 'KZT',
        currencyDisplay: 'symbol',
        maximumFractionDigits: 2,
      }).format(price);
    },
    formatNumber(n) {
      if (n === null || n === undefined) return '0';
      return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(n);
    },
  },
};
</script>

<style scoped>
.campaign-moderation--rtb {
  overflow-x: hidden;
}

.moderation-card--rtb-campaign {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: start;
  gap: 16px;
}

.moderation-card--rtb-campaign .moderation-card__main {
  min-width: 0;
}

.moderation-card--rtb-campaign .moderation-card__meta-row {
  flex-wrap: wrap;
  gap: 8px;
}

.moderation-card--rtb-campaign .moderation-card__actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-shrink: 0;
  align-self: start;
}

.moderation-card--rtb-campaign .moderation-card__stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-top: 4px;
}

.stat {
  padding: 8px 10px;
  background: #f8fafc;
  border: 1px solid #eef2f7;
  border-radius: 8px;
  min-width: 0;
}

.stat-label {
  font-size: 11px;
  color: #64748b;
  line-height: 1.3;
}

.stat-value {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chip--package {
  background: #eff6ff;
  color: #1e40af;
  border-color: rgba(30, 64, 175, 0.12);
}

.chip--truncate {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 12px;
  line-height: 1.2;
}

.status-pill--ok {
  border-color: #16a34a;
  color: #166534;
}

.status-pill--fail {
  border-color: #dc2626;
  color: #991b1b;
}

.status-pill--pending {
  border-color: #2563eb;
  color: #1d4ed8;
}

.status-pill--idle {
  border-color: #6b7280;
  color: #4b5563;
}

.status-pill--archived {
  border-color: #9ca3af;
  color: #4b5563;
  background: #f9fafb;
}

@media (max-width: 768px) {
  .moderation-card--rtb-campaign {
    grid-template-columns: 1fr;
  }

  .moderation-card--rtb-campaign .moderation-card__actions {
    flex-direction: row;
    flex-wrap: wrap;
    padding-top: 8px;
    border-top: 1px solid #eef2f7;
  }

  .moderation-card--rtb-campaign .moderation-card__stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
